/**
 * 이상한 방 - 꾸미기 페이즈 에디터 (배치 입력 루프)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3.2절(표면 스냅) · 4.3절(원칙 2의 집행) · 5절(변화 규칙)
 *           컨셉시트 4.1절(꾸미기)
 *
 * **입력을 `core/verbs.ts`의 검증을 거친 새 `RoomState`로 바꾸고, 그 결과만 화면에 반영한다.**
 * 그래서 이 파일에는 게임 규칙이 한 줄도 없다 - 규칙을 한 조각이라도 복제하는 순간 화면과 서버가
 * 갈라지고, 그 갈라짐은 2주차에 서버 권위를 붙이는 순간 판정 버그로 터진다.
 *
 * `EditorState`는 **절대 제자리 변경하지 않는다.** 전이는 새 객체를 만들어 `setState`로 교체한다.
 * 덕분에 `syncObjectViews`가 참조 비교 하나로 갱신 대상을 가려낸다(objectView.ts).
 * 예외는 둘뿐이며 둘 다 도메인 상태가 아니다: Three.js 렌더 객체와 포인터 드래그 추적기.
 *
 * 좌클릭의 의미는 **도구**가 정한다. 배치와 선택이 한 버튼을 두고 다투는 것이 이런 에디터의
 * 고질적 모호함이고 표면 스냅(3.2절)이 그걸 악화시킨다 - 책상 클릭이 "집는다"와 "위에 놓는다"
 * 양쪽으로 읽힌다. 배치 도구의 좌클릭은 항상 배치, 선택 도구의 좌클릭은 항상 선택/이동이다.
 *
 * ## 입력 경로가 둘인 이유 (`inputMode`)
 *
 * 아이소메트릭 전환으로 조작의 주 무대가 **손가락**이 됐고, 마우스 경로의 전제 하나가 통째로 깨졌다:
 * **터치에는 hover가 없다.** 마우스는 "움직여서 고스트를 보고 → 누르면 확정"이지만,
 * 손가락은 화면에 닿는 순간이 곧 누르는 순간이라 그 사이가 없다.
 *
 *  - `'pointer'` - 기존 경로. 캔버스 포인터 이벤트를 직접 듣는다(데스크톱 마우스).
 *  - `'tap'`     - 캔버스 이벤트를 **듣지 않는다.** 탭 판정은 `pointerInput`+`tapPicker`가 하고,
 *                  `app/inputLayer.ts`가 그 결과 셀을 `aimAt()`으로 넘긴다. 확정은 별도 동작(`commitAim`)이다.
 *
 * 탭 모드가 **2단계(위치 지정 → 확인)**인 것은 원칙 2의 연장이다. 한 번의 탭으로 즉시 놓으면
 * 손가락이 그 자리를 가린 채 배치가 끝나 "무엇이 어디에 놓였는지"를 놓기 전에 볼 수 없다.
 * 지정과 확정을 가르면 그 사이에 줌·회전·팬으로 확인하고 회전·색까지 바꿀 수 있다.
 */

import type { Object3D } from 'three'

import { buildOccupancy, countByPlayer, createRoom, getObject, type Catalog } from '../core/room'
import {
  applyAdd,
  applyMove,
  applyRecolor,
  applyRemove,
  applyRotate,
  canPlace,
  type VerbContext,
  type VerbResult,
} from '../core/verbs'
import {
  ROT_STEP_COUNT,
  type CatalogEntry,
  type CatalogId,
  type Cell3,
  type InstanceId,
  type ObjectInstance,
  type PaletteIndex,
  type PlayerId,
  type RoomBounds,
  type RoomConfig,
  type RoomState,
  type Rot,
} from '../core/types'
import { PALETTE, PALETTE_SIZE } from '../catalog/palette'
import { createGhostMesh, updateGhost } from '../render/gridHelper'
import { disposeObject3D, syncObjectViews } from '../render/objectView'
import type { SceneCtx } from '../render/scene'
import {
  createSelectionOutline,
  disposeSelectionOutline,
  placeSelectionOutline,
} from '../render/selectionOutline'
import { pickCell, type PickResult } from './picker'
import { createHud, type HudHandle, type HudView } from '../ui/hud'

/* --- 입력 상수 ------------------------------------------------------------------ */

/** 마우스 주 버튼(PointerEvent.button). 카메라는 우/휠 버튼을 쓴다(pointerInput.ts). */
const PRIMARY_BUTTON = 0
// 회전 단계 수(4절: 90° 4단계 이산값)는 `core/types.ROT_STEP_COUNT` 하나다 - 사본을 들지 않는다(P1 정리).
/** 회전 동사가 잠기는 대칭 차수(4.2절: 4 = 완전 대칭 → 회전 전면 거부). */
const FULLY_SYMMETRIC = 4

/**
 * 클릭과 드래그를 가르는 이동량(CSS 픽셀). 0이면 손떨림이 전부 드래그가 되고("놓으려던 게 옮겨졌다"),
 * 크면 짧은 이동 드래그가 클릭으로 처리돼 엉뚱한 곳에 배치된다.
 */
const DRAG_THRESHOLD_PX = 6

/** 숫자 단축키 → 카탈로그 순번. HUD 목록 순서와 같아야 한다(hud.ts가 같은 규칙으로 라벨을 단다). */
const DIGIT_SHORTCUTS = '1234567890'

/* --- 타입 ----------------------------------------------------------------------- */

/** 좌클릭의 의미를 결정하는 도구. `place`는 소품을 놓는 상태, `select`는 놓인 물건을 다루는 상태다. */
type EditorTool = Readonly<{ kind: 'place'; catalogId: CatalogId }> | Readonly<{ kind: 'select' }>

/**
 * 에디터의 전체 상태. **불변**이며 `setState`로만 교체된다.
 * `occupancy`가 파생값인데도 여기 있는 것은 의도다 - 포인터가 움직일 때마다 `canPlace`가
 * 필요로 하는데(3.1절) 매번 다시 만들면 오브젝트 수 × footprint가 프레임마다 돈다.
 * 대신 생성을 `withRoom()` 하나에 묶어 `room`과 어긋날 경로를 없앴다.
 */
type EditorState = Readonly<{
  room: RoomState
  occupancy: ReadonlyMap<string, InstanceId>
  tool: EditorTool
  selectedId: InstanceId | null
  /** 배치 모드에서 새 오브젝트에 쓸 색. 선택 모드에서는 HUD가 선택된 물건의 색을 대신 보여 준다. */
  paintColorId: PaletteIndex
  /** 배치 고스트의 회전. 놓기 전에 미리 돌려 볼 수 있어야 1분 안에 방향을 맞출 수 있다. */
  ghostRot: Rot
  /** 포인터가 가리키는 셀. 가리키는 것이 없으면 `null`(고스트를 감춘다). */
  ghostCell: Cell3 | null
  /** 수정키가 눌려 자유 높이 모드인가(3.2절: 기본값은 표면 스냅). */
  isFreeHeight: boolean
  /** 자유 높이 모드가 잠근 그리드 층. 표면 스냅 상태면 `null`. */
  freeHeightLevel: number | null
}>

/** 드래그 추적기. 입력 장치의 순간 상태이며 도메인 상태가 아니다. */
type DragTracker = Readonly<{
  pointerId: number
  startX: number
  startY: number
  isDragging: boolean
}>

/**
 * 탭 한 번이 실제로 무엇을 했는가. 호출부(`app/inputLayer.ts`)가 화면 피드백을 고르는 데 쓴다 -
 * 확정과 지정이 같은 연출을 쓰면 "눌렀는데 놓였는지 아닌지"를 알 수 없다.
 */
export type AimOutcome = 'aimed' | 'committed' | 'selected' | 'ignored'

/**
 * 지금 확정하면 무슨 일이 일어나는가. 모바일 확인 바가 이 값만 보고 그려진다.
 *
 * `canCommit`이 핵심이다 - 4.3절은 거부를 "오류 메시지가 아니라 UI에서 아예 선택 불가"로
 * 처리하라고 못박는다. 확인 버튼이 미리 꺼져 있으면 1분짜리 페이즈에서 헛탭이 사라진다.
 */
export type EditorAim = Readonly<{
  action: 'add' | 'move' | 'none'
  cell: Cell3 | null
  canCommit: boolean
  /** 놓거나 옮길 대상의 이름. 없으면 `null`. */
  subjectName: string | null
  rot: Rot
  /** 대상이 완전 대칭이라 회전 동사가 잠겨 있는가(4.2절). */
  isRotateLocked: boolean
}>

export type EditorDeps = Readonly<{
  ctx: SceneCtx
  bounds: RoomBounds
  catalog: Catalog
  config: RoomConfig
  playerId: PlayerId
  templateId: string
  /** HUD를 붙일 DOM 컨테이너. 보통 `document.body`. */
  hudMount: HTMLElement
  /** 시작 방 상태. 생략하면 빈 방(1주차 동작). 시드 생성 방을 그대로 이어받을 때 쓴다. */
  initialRoom?: RoomState
  /**
   * 포인터가 가리키는 셀이 바뀔 때마다 알린다.
   * 그리드 오버레이가 커서 주변에만 나타나야 하는데(gridHelper), 그 위치를 아는 것은 에디터뿐이다.
   */
  onPointerCell?: (cell: Cell3 | null) => void
  /** 만들자마자 입력을 받을지. 관찰 모드로 시작하는 앱은 `false`로 두고 나중에 켠다. */
  startActive?: boolean
  /**
   * 입력 경로. 기본값 `'pointer'`(마우스 hover 전제).
   * `'tap'`이면 캔버스 포인터 리스너를 **아예 붙이지 않고** `aimAt`/`commitAim`만 받는다 -
   * 붙여 두면 제스처 인식기(`pointerInput.ts`)와 같은 이벤트를 두 곳에서 해석해
   * 카메라를 끌려던 드래그가 배치로 확정되는 사고가 난다.
   */
  inputMode?: 'pointer' | 'tap'
}>

export type EditorHandle = Readonly<{
  /** 현재 방 상태 스냅샷. 2주차에 서버로 올리거나 디버그로 들여다볼 때 쓴다. */
  getRoom: () => RoomState
  /**
   * 편집 대상 방을 통째로 갈아 끼운다. **배치 모드로 들어올 때 호출부가 부른다.**
   *
   * 게임 모드에서는 화면의 방을 세션(`game/session.ts`)이 소유하고 원본↔변경본을 갈아 끼운다.
   * 그동안 에디터의 `state.room`은 라운드 시작 시점 그대로 얼어 있으므로, 그 상태로 배치 모드에
   * 들어오면 **화면과 편집 대상이 다른 방**이 된다 - 소파를 클릭했는데 상태에는 없는 소파라
   * 선택이 안 되고, 빈 칸이라 믿고 놓으면 화면의 물건과 겹친다.
   *
   * 점유 맵도 함께 다시 만든다(`withRoom` 경유). 이 함수를 우회해 방을 바꾸면
   * 점유 맵이 낡은 채로 남아 3.1절 검증이 통째로 거짓이 된다.
   */
  setRoom: (room: RoomState) => void
  /**
   * 에디터가 만든 HUD. 관찰 쪽 표시(탄약·모드·시드)를 쓰려는 호출부에 빌려준다.
   * 바깥에서 만들어 주입하지 않는 이유: HUD의 출력은 카탈로그·팔레트 클릭이고 그 클릭을
   * 처리하는 것은 에디터뿐이라, 주입식으로 하면 핸들러를 나중에 꽂는 순환 배선이 된다.
   */
  hud: HudHandle
  /**
   * 입력 수신 on/off. **관찰(수색) 중에는 반드시 꺼야 한다** - 리스너가 window에 걸려 있어서
   * 끄지 않으면 R이 소품을 돌리고 탭이 물건을 배치한다.
   * 리스너를 뗐다 붙이지 않고 플래그로 막는 이유는 등록·해제 순서가 어긋나면 입력이 조용히 죽어서다.
   */
  setActive: (active: boolean) => void
  isActive: () => boolean
  dispose: () => void

  /* --- 탭 경로(아이소메트릭·모바일). `inputMode: 'tap'`에서 호출부가 쓰는 전부다. --- */

  /**
   * 탭이 가리킨 셀을 **배치 예정 위치로 지정**한다(아직 확정하지 않는다).
   *
   * `overInstanceId`는 탭 지점에 있던 오브젝트다(`tapPicker`의 `place` 결과가 알려 준다).
   * 선택 도구에서 아직 아무것도 안 집었으면 그 물건을 집는 것이 탭의 의미가 되고,
   * 이미 지정된 칸을 **다시 탭하면 확정**된다 - 확인 버튼과 정확히 같은 경로를 탄다.
   */
  aimAt: (cell: Cell3, overInstanceId?: InstanceId) => AimOutcome
  /** 지정된 위치를 확정한다(추가 또는 이동). 검증은 전부 `verbs.ts`를 거친다. */
  commitAim: () => boolean
  /** 지정을 취소한다. 선택은 유지한다 - 취소는 "여기 말고"이지 "그 물건 말고"가 아니다. */
  clearAim: () => void
  getAim: () => EditorAim

  /* --- HUD 밖(모바일 버튼·제스처)에서 들어오는 동사. 키보드와 같은 함수를 부른다. --- */

  pickCatalog: (id: CatalogId) => void
  pickSelectTool: () => void
  /** 선택된 물건이 있으면 그 물건을, 없으면 배치 고스트를 90° 돌린다(키보드 R과 동일). */
  rotateSubject: () => void
  removeSelected: () => void
  setColor: (id: PaletteIndex) => void
}>

/* --- 순수 헬퍼 ------------------------------------------------------------------ */

function clampInt(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** 다음 회전 단계·다음 색. 둘 다 이산값이라 나머지 연산 하나로 끝난다(4절의 부수 효과). */
function nextRot(rot: Rot): Rot {
  return ((rot + 1) % ROT_STEP_COUNT) as Rot
}

function nextColorId(colorId: PaletteIndex): PaletteIndex {
  return (colorId + 1) % PALETTE_SIZE
}

function isSameCell(a: Cell3, b: Cell3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z
}

/** Shift 또는 Alt. 둘 다 받는 이유는 OS·브라우저마다 한쪽이 시스템 제스처에 물려 있어서다. */
function isFreeHeightModifier(ev: { shiftKey: boolean; altKey: boolean }): boolean {
  return ev.shiftKey || ev.altKey
}

/*
 * 선택 외곽선은 `render/selectionOutline.ts`로 옮겼다(로드맵 M4). 사람 체인저의 도구(`changerTool.ts`)가 같은
 * 외곽선을 쓰게 되어, 두 파일이 각자 만들면 "고른 물건"이 꾸미기와 바꾸기에서 다른 물건으로 보인다.
 */

/**
 * `InstanceId` 발급기. `crypto.randomUUID()`는 보안 컨텍스트에서만 존재하므로
 * (LAN IP + http 플레이테스트에서 사라진다) 폴백이 필수다.
 * 2절이 금지하는 것은 **재사용**뿐이라 단조 증가 카운터면 충분하다.
 */
function createInstanceIdFactory(): () => InstanceId {
  let counter = 0
  const sessionTag = Date.now().toString(36)

  return () => {
    const api = globalThis.crypto
    if (typeof api?.randomUUID === 'function') return api.randomUUID()

    counter += 1
    return `inst-${sessionTag}-${counter}`
  }
}

/* --- 에디터 --------------------------------------------------------------------- */

/** 꾸미기 에디터를 만들어 입력 리스너를 붙인다. 정리는 반환된 핸들의 `dispose()`로만 한다(리스너·HUD·GPU 자원이 전부 묶여 있다). */
export function createEditor(deps: EditorDeps): EditorHandle {
  const { ctx, bounds, catalog, config, playerId } = deps
  const canvas = ctx.renderer.domElement
  const nextInstanceId = createInstanceIdFactory()

  const catalogList = [...catalog.values()]
  const firstEntry = catalogList[0]
  if (firstEntry === undefined) {
    // 소품이 없으면 꾸미기 자체가 성립하지 않는다. 조용히 빈 화면을 띄우는 것보다 즉시 터지는 편이 싸다.
    throw new Error('createEditor: 카탈로그가 비어 있다 - 배치할 소품이 하나도 없다')
  }

  const ghost: Object3D = createGhostMesh()
  const outline = createSelectionOutline()
  ctx.scene.add(ghost, outline)

  /** 포인터 이동마다 도는 경로의 예외를 한 번만 보고하기 위한 래치(로그 폭주 방지용 진단 플래그). */
  let hasReportedPickFailure = false
  let drag: DragTracker | null = null
  let active = deps.startActive ?? true

  const initialRoom = deps.initialRoom ?? createRoom(deps.templateId)

  let state: EditorState = {
    room: initialRoom,
    // 생성된 방을 받았다면 점유 맵도 그 방의 것이어야 한다. 빈 맵으로 두면
    // 첫 배치가 이미 가구가 있는 칸을 "비어 있다"고 판정해 소품이 겹친다(3.1절 위반).
    occupancy: buildOccupancy(initialRoom, catalog),
    tool: { kind: 'place', catalogId: firstEntry.id },
    selectedId: null,
    paintColorId: firstEntry.defaultColorId,
    ghostRot: 0,
    ghostCell: null,
    isFreeHeight: false,
    freeHeightLevel: null,
  }

  const hud = createHud({
    mount: deps.hudMount,
    catalog: catalogList,
    palette: PALETTE,
    handlers: {
      onPickCatalog: selectCatalogEntry,
      onPickSelectTool: pickSelectTool,
      onPickColor: applyColor,
    },
  })
  /* --- 상태 전이 --------------------------------------------------------------- */

  /**
   * 방 상태를 갈아 끼운다. **점유 맵을 여기서만 만든다** -
   * 이 함수를 우회해 `room`을 바꾸면 점유 맵이 낡은 채로 남아 검증이 통째로 거짓이 된다.
   */
  function withRoom(current: EditorState, room: RoomState): EditorState {
    return { ...current, room, occupancy: buildOccupancy(room, catalog) }
  }

  /**
   * 자유 높이 모드 전이(3.2절). 켜지는 순간 **지금 가리키던 표면의 높이를 그대로 잠근다.**
   * 이 잠금이 UX의 전부다 - 책상을 가리킨 채 Shift를 누르고 포인터를 책상 밖으로 밀면
   * 그 높이 그대로 허공에 남는다. 공중부양이 배워야 하는 조작이 아니라 한 동작이 된다.
   */
  function withFreeHeight(current: EditorState, isFree: boolean): EditorState {
    if (current.isFreeHeight === isFree) return current
    if (!isFree) return { ...current, isFreeHeight: false, freeHeightLevel: null }

    return { ...current, isFreeHeight: true, freeHeightLevel: current.ghostCell?.y ?? 0 }
  }

  /**
   * 상태를 교체하고 화면·HUD에 반영한다. `room`이 그대로면 `syncObjectViews`를 건너뛴다 -
   * 포인터 이동마다 도는 경로라 매번 부르면 오브젝트 수만큼의 Map 할당이 프레임마다 발생한다.
   */
  function setState(next: EditorState): void {
    const previous = state
    state = next

    if (next.room !== previous.room) syncObjectViews(ctx, next.room, catalog)

    updateGhostView(next)
    updateSelectionOutline(next)
    deps.onPointerCell?.(next.ghostCell)
    hud.render(buildHudView(next))
  }

  /** 동사 결과를 반영한다. 거부는 삼키지 않고 반드시 HUD로 알린다(4.3절). */
  function commitVerb(result: VerbResult, nextSelectedId?: InstanceId | null): void {
    if (!result.ok) {
      hud.showRejection(result.reason)
      return
    }

    const applied = withRoom(state, result.state)
    setState(nextSelectedId === undefined ? applied : { ...applied, selectedId: nextSelectedId })
  }

  function verbContext(current: EditorState): VerbContext {
    return {
      catalog,
      config,
      bounds,
      occupancy: current.occupancy,
      // 5.1절("한 물건은 한 번만 건드릴 수 있다")은 체인저 페이즈의 규칙이다.
      // 1주차 꾸미기에는 체인저가 없으므로 끈 채로 둔다 - 2주차에 여기만 켜면 된다.
      shouldEnforceOneChangePerObject: false,
      // 꾸미기 페이즈이므로 인당 배치 수 제한을 건다(컨셉시트 4.1).
      shouldEnforcePlacementLimit: true,
    }
  }

  /* --- 화면 반영 --------------------------------------------------------------- */

  /** 고스트가 무엇을 미리 보여 줄지 정한다. 배치 모드는 "놓을 소품", 선택 모드는 "옮길 물건". */
  function resolveGhostSubject(
    current: EditorState,
  ): Readonly<{ entry: CatalogEntry; rot: Rot; ignoredId?: InstanceId }> | null {
    if (current.tool.kind === 'place') {
      const entry = catalog.get(current.tool.catalogId)
      return entry === undefined ? null : { entry, rot: current.ghostRot }
    }

    if (current.selectedId === null) return null

    const obj = getObject(current.room, current.selectedId)
    if (obj === undefined) return null

    const entry = catalog.get(obj.catalogId)
    // 자기 자신이 이미 점유한 칸은 충돌로 치지 않는다 - 그러지 않으면 제자리 근처로 못 옮긴다.
    return entry === undefined ? null : { entry, rot: obj.rot, ignoredId: obj.id }
  }

  /** 고스트 갱신. 녹/적은 `canPlace()`(verbs.ts) 결과 그대로다 - 복제하면 "초록인데 안 놓이는" 상태가 생긴다. */
  function updateGhostView(current: EditorState): void {
    const subject = resolveGhostSubject(current)
    if (subject === null || current.ghostCell === null) {
      ghost.visible = false
      return
    }

    const check = canPlace(
      current.room,
      catalog,
      subject.entry,
      current.ghostCell,
      subject.rot,
      bounds,
      current.occupancy,
      subject.ignoredId,
    )

    updateGhost(ghost, current.ghostCell, subject.entry, subject.rot, check.ok)
  }

  function updateSelectionOutline(current: EditorState): void {
    const obj = current.selectedId === null ? undefined : getObject(current.room, current.selectedId)
    const entry = obj === undefined ? undefined : catalog.get(obj.catalogId)
    placeSelectionOutline(outline, obj, entry)
  }

  function buildHudView(current: EditorState): HudView {
    const selected =
      current.selectedId === null ? undefined : getObject(current.room, current.selectedId)
    const entry = selected === undefined ? undefined : catalog.get(selected.catalogId)

    return {
      toolKind: current.tool.kind,
      activeCatalogId: current.tool.kind === 'place' ? current.tool.catalogId : null,
      // 선택 중이면 그 물건의 색을 보여 준다 - 팔레트 클릭이 곧 그 물건의 색 변경이기 때문이다.
      activeColorId: selected?.colorId ?? current.paintColorId,
      selectedName: entry?.name ?? null,
      isRotateLocked: entry?.rotSymmetry === FULLY_SYMMETRIC,
      placedCount: countByPlayer(current.room, playerId),
      placementLimit: config.placementsPerPlayer,
      isFreeHeight: current.isFreeHeight,
      freeHeightLevel: current.freeHeightLevel,
      pointerCell: current.ghostCell,
    }
  }

  /**
   * 지금 확정하면 무슨 일이 일어나는가.
   *
   * **판정을 새로 쓰지 않는다** - 고스트의 녹/적과 똑같이 `canPlace`(verbs.ts) 하나를 본다.
   * 두 벌이 되면 "초록인데 확인 버튼이 꺼져 있는" 상태가 생기고, 그건 규칙이 아니라 버그로 읽힌다.
   */
  function buildAim(current: EditorState): EditorAim {
    const action: EditorAim['action'] =
      current.tool.kind === 'place' ? 'add' : current.selectedId === null ? 'none' : 'move'
    const subject = resolveGhostSubject(current)
    const cell = current.ghostCell

    if (subject === null || cell === null) {
      return {
        action,
        cell,
        canCommit: false,
        subjectName: subject?.entry.name ?? null,
        rot: current.ghostRot,
        isRotateLocked: subject?.entry.rotSymmetry === FULLY_SYMMETRIC,
      }
    }

    const check = canPlace(
      current.room,
      catalog,
      subject.entry,
      cell,
      subject.rot,
      bounds,
      current.occupancy,
      subject.ignoredId,
    )

    // 제자리 이동은 규칙 위반이 아니라 "아무것도 안 한 것"이다(moveSelectedTo와 같은 판단).
    // 배치 한도는 canPlace가 보지 않으므로(applyAdd의 몫) 여기서 같이 본다 - 버튼이 눌린 뒤에
    // 한도 초과를 알려 주면 4.3절이 말하는 "선택 불가"가 아니라 그냥 오류 메시지가 된다.
    const selected =
      current.selectedId === null ? undefined : getObject(current.room, current.selectedId)
    const isNoOp = action === 'move' && selected !== undefined && isSameCell(selected.cell, cell)
    const hasBudget =
      action !== 'add' || countByPlayer(current.room, playerId) < config.placementsPerPlayer

    return {
      action,
      cell,
      canCommit: check.ok && !isNoOp && hasBudget,
      subjectName: subject.entry.name,
      rot: subject.rot,
      isRotateLocked: subject.entry.rotSymmetry === FULLY_SYMMETRIC,
    }
  }

  /* --- 동사 실행 --------------------------------------------------------------- */

  function selectCatalogEntry(catalogId: CatalogId): void {
    const entry = catalog.get(catalogId)
    if (entry === undefined) {
      hud.showRejection('unknown-catalog')
      return
    }

    setState({
      ...state,
      tool: { kind: 'place', catalogId },
      selectedId: null,
      // 카탈로그가 정해 둔 기본색을 존중한다. 회색 박스 단계에서 소품 종류를 색으로라도 가르는 장치다.
      paintColorId: entry.defaultColorId,
      ghostRot: 0,
    })
  }

  /**
   * 선택 도구로 전환하며 **선택과 지정을 함께 비운다** - Esc와 정확히 같은 동작이다.
   *
   * 선택을 남기지 않는 것이 요점이다. 폰에는 Esc가 없어서, 남겨 두면 한번 집은 물건을
   * 놓아 줄 방법이 사라지고 그 뒤의 모든 탭이 "그 물건을 저기로 옮길까?"로만 해석된다.
   * HUD의 '선택 도구' 버튼이 그 탈출구를 겸한다(버튼에 붙은 `Esc` 라벨이 그 뜻이다).
   */
  function pickSelectTool(): void {
    setState({ ...state, tool: { kind: 'select' }, selectedId: null, ghostCell: null })
  }

  function placeAt(cell: Cell3): void {
    if (state.tool.kind !== 'place') return

    const entry = catalog.get(state.tool.catalogId)
    if (entry === undefined) {
      hud.showRejection('unknown-catalog')
      return
    }

    const obj: ObjectInstance = {
      id: nextInstanceId(),
      catalogId: entry.id,
      cell,
      rot: state.ghostRot,
      colorId: state.paintColorId,
      placedBy: playerId,
    }

    commitVerb(applyAdd(state.room, obj, verbContext(state)))
  }

  function moveSelectedTo(cell: Cell3): void {
    const selectedId = state.selectedId
    if (selectedId === null) return

    // 제자리 놓기는 규칙 위반이 아니라 "아무것도 안 한 것"이다. verbs.ts는 no-visible-change로
    // 거부하지만(4.3절) 그 문구를 띄우면 집었다 놓기만 해도 경고가 뜬다.
    const obj = getObject(state.room, selectedId)
    if (obj !== undefined && isSameCell(obj.cell, cell)) return

    commitVerb(applyMove(state.room, selectedId, cell, verbContext(state)))
  }

  /** 회전. 선택된 물건이 있으면 그 물건을, 없으면 배치 고스트를 돌린다(고스트는 아직 방에 없어 검증 대상이 아니다). */
  function rotateSelected(): void {
    if (state.selectedId === null) {
      setState({ ...state, ghostRot: nextRot(state.ghostRot) })
      return
    }

    const obj = getObject(state.room, state.selectedId)
    if (obj === undefined) return

    commitVerb(applyRotate(state.room, state.selectedId, nextRot(obj.rot), verbContext(state)))
  }

  /** 색 지정. 선택된 물건이 있으면 색 변경 동사, 없으면 앞으로 놓을 색을 바꾼다. */
  function applyColor(colorId: PaletteIndex): void {
    if (state.selectedId === null) {
      setState({ ...state, paintColorId: colorId })
      return
    }

    commitVerb(applyRecolor(state.room, state.selectedId, colorId, verbContext(state)))
  }

  function cycleColor(): void {
    if (state.selectedId === null) {
      applyColor(nextColorId(state.paintColorId))
      return
    }

    const obj = getObject(state.room, state.selectedId)
    if (obj === undefined) return

    applyColor(nextColorId(obj.colorId))
  }

  /** 제거 성공 시 선택을 반드시 비운다 - 없는 id를 들고 있으면 이후 동사가 전부 unknown-instance가 된다. */
  function removeSelected(): void {
    if (state.selectedId === null) return
    commitVerb(applyRemove(state.room, state.selectedId, verbContext(state)), null)
  }

  /* --- 탭 경로 (2단계: 지정 → 확인) ---------------------------------------------- */

  /**
   * 탭이 가리킨 셀을 지정한다. **손가락은 hover가 없으므로 지정과 확정을 가른다**(파일 상단 참고).
   *
   * 세 갈래뿐이다:
   *  1. 선택 도구인데 아무것도 안 집었다 → 탭한 물건을 집는다(집는 것이 곧 미리보기다)
   *  2. 이미 지정된 칸을 다시 탭했다 → 확정(확인 버튼과 같은 경로)
   *  3. 그 외 → 지정만 하고 고스트를 그 자리에 세운다
   */
  function aimAt(cell: Cell3, overInstanceId?: InstanceId): AimOutcome {
    if (!active) return 'ignored'

    if (state.tool.kind === 'select' && state.selectedId === null) {
      if (overInstanceId === undefined) return 'ignored'

      const obj = getObject(state.room, overInstanceId)
      if (obj === undefined) return 'ignored'

      setState({ ...state, selectedId: overInstanceId, ghostCell: obj.cell })
      return 'selected'
    }

    // 자기 자신을 가리켰다면 표면 스냅이 "자기 위"를 돌려준 것이다. 그대로 쓰면 집기만 해도 떠오른다.
    const target =
      overInstanceId !== undefined && overInstanceId === state.selectedId
        ? (getObject(state.room, overInstanceId)?.cell ?? cell)
        : cell

    if (state.ghostCell !== null && isSameCell(state.ghostCell, target) && buildAim(state).canCommit) {
      return commitAim() ? 'committed' : 'aimed'
    }

    setState({ ...state, ghostCell: target })
    return 'aimed'
  }

  /**
   * 지정을 확정한다. 확정 뒤 고스트를 걷는 이유: 추가 직후 그 칸은 이제 차 있어서
   * 고스트가 빨갛게 남는데, 방금 성공한 조작 자리에 실패색이 뜨면 실패한 것으로 읽힌다.
   */
  function commitAim(): boolean {
    if (!active) return false

    const cell = state.ghostCell
    if (cell === null) return false

    const before = state.room
    if (state.tool.kind === 'place') placeAt(cell)
    else moveSelectedTo(cell)

    if (state.room === before) return false

    setState({ ...state, ghostCell: null })
    return true
  }

  function clearAim(): void {
    if (state.ghostCell === null) return
    setState({ ...state, ghostCell: null })
  }

  /* --- 포인터 ------------------------------------------------------------------ */

  /**
   * 포인터 → 셀 변환. `pickCell`은 좌표가 유한하지 않으면 던지는데 그 경로가 포인터 이동마다
   * 돌기 때문에, 삼키지 않되(최초 1회 로그 + 알림) 이후에는 "가리키는 것 없음"으로 처리한다.
   */
  function safePick(ev: PointerEvent, current: EditorState): PickResult | null {
    try {
      return pickCell(
        ctx,
        ev,
        current.room,
        catalog,
        bounds,
        current.isFreeHeight,
        current.freeHeightLevel ?? undefined,
      )
    } catch (error) {
      if (!hasReportedPickFailure) {
        hasReportedPickFailure = true
        console.error('[editor] 포인터 → 셀 변환 실패. 이후 동일 오류는 생략한다.', error)
        hud.showNotice('포인터 위치를 계산하지 못했다')
      }
      return null
    }
  }

  /**
   * 고스트가 설 셀. 표면 스냅은 "가리킨 물건 **위**"를 돌려주는데(picker.ts), 지금 선택된 물건을
   * 가리키고 있다면 그건 "자기 자신 위"라 그대로 쓰면 집기만 해도 한 칸 떠오른다.
   * 그 경우에는 물건의 현재 셀을 그대로 쓴다.
   */
  function resolveGhostCell(current: EditorState, pick: PickResult | null): Cell3 | null {
    if (pick === null) return null
    if (current.selectedId === null || pick.instanceId !== current.selectedId) return pick.cell

    return getObject(current.room, current.selectedId)?.cell ?? pick.cell
  }

  /**
   * 포인터 캡처를 시도한다. **실패해도 조작은 계속돼야 한다.**
   * 캡처는 "캔버스 밖에서 손을 떼도 pointerup을 받는다"는 편의일 뿐인데 브라우저가 던질 수 있고,
   * 막지 않으면 pointerdown 핸들러 전체가 중단돼 **클릭이 아예 먹지 않는다.**
   */
  function tryCapturePointer(pointerId: number): void {
    try {
      canvas.setPointerCapture(pointerId)
    } catch (error) {
      console.warn('[editor] 포인터 캡처에 실패했다. 캔버스 밖에서 손을 떼면 드래그가 끊길 수 있다.', error)
    }
  }

  function handlePointerDown(ev: PointerEvent): void {
    if (!active) return
    if (ev.button !== PRIMARY_BUTTON) return
    // 멀티터치에서 두 번째 손가락이 첫 번째 드래그를 가로채지 못하게 한다.
    if (drag !== null) return

    tryCapturePointer(ev.pointerId)
    drag = { pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY, isDragging: false }

    const withMode = withFreeHeight(state, isFreeHeightModifier(ev))
    const pick = safePick(ev, withMode)

    // 선택 도구에서 물건을 누르면 즉시 선택된다. 누르자마자 선택돼야 그대로 끌어 옮길 수 있다.
    const selectedId =
      withMode.tool.kind === 'select' && pick?.instanceId !== undefined
        ? pick.instanceId
        : withMode.selectedId

    const withSelection: EditorState = { ...withMode, selectedId }
    setState({ ...withSelection, ghostCell: resolveGhostCell(withSelection, pick) })
  }

  function handlePointerMove(ev: PointerEvent): void {
    if (!active) return

    const withMode = withFreeHeight(state, isFreeHeightModifier(ev))
    const pick = safePick(ev, withMode)

    if (drag !== null && !drag.isDragging && drag.pointerId === ev.pointerId) {
      const moved = Math.hypot(ev.clientX - drag.startX, ev.clientY - drag.startY)
      if (moved > DRAG_THRESHOLD_PX) drag = { ...drag, isDragging: true }
    }

    setState({ ...withMode, ghostCell: resolveGhostCell(withMode, pick) })
  }

  function handlePointerUp(ev: PointerEvent): void {
    if (!active) return
    if (drag === null || drag.pointerId !== ev.pointerId) return
    // 마우스는 버튼이 몇 개든 pointerId가 하나뿐이라, 좌버튼을 누른 채 우/휠 버튼을 떼면
    // 그 pointerup이 여기까지 들어온다. 버튼을 보지 않으면 손도 떼지 않았는데 배치가 확정된다.
    if (ev.button !== PRIMARY_BUTTON) return

    const wasDragging = drag.isDragging
    drag = null
    // 캡처가 실패했거나 브라우저가 이미 풀었을 수 있다. 있을 때만 푼다.
    if (canvas.hasPointerCapture(ev.pointerId)) canvas.releasePointerCapture(ev.pointerId)

    const cell = state.ghostCell
    if (cell === null) return

    // 드래그로 끝난 좌클릭은 배치로 치지 않는다(카메라를 돌리려다 놓는 사고를 막는다).
    if (state.tool.kind === 'place') {
      if (!wasDragging) placeAt(cell)
      return
    }

    moveSelectedTo(cell)
  }

  function handlePointerCancel(ev: PointerEvent): void {
    if (drag?.pointerId === ev.pointerId) drag = null
  }

  function handlePointerLeave(): void {
    if (!active) return
    // 드래그 중에는 캡처가 걸려 있으므로 커서가 나가도 조작은 계속된다. 고스트를 지우면 안 된다.
    if (drag !== null) return
    setState({ ...state, ghostCell: null })
  }

  /* --- 키보드 ------------------------------------------------------------------ */

  /**
   * 자유 높이 층 조절. 수정키를 누른 동안에만 듣는다 - 자유 높이 여부는 포인터 이벤트의
   * 수정키 상태에서 나오므로, 키만으로 켜면 다음 포인터 이동에서 곧바로 꺼진다.
   */
  function adjustFreeHeight(delta: number): void {
    if (!state.isFreeHeight || state.freeHeightLevel === null) return

    const level = clampInt(state.freeHeightLevel + delta, 0, bounds.sizeY - 1)
    if (level === state.freeHeightLevel) return

    // 고스트의 x·z는 다음 포인터 이동에서 다시 계산된다.
    // y만 먼저 옮겨 키를 누른 즉시 높이 변화가 보이게 한다.
    const cell = state.ghostCell === null ? null : { ...state.ghostCell, y: level }
    setState({ ...state, freeHeightLevel: level, ghostCell: cell })
  }

  function selectCatalogByDigit(key: string): boolean {
    const entry = catalogList[DIGIT_SHORTCUTS.indexOf(key)]
    if (entry === undefined) return false

    selectCatalogEntry(entry.id)
    return true
  }

  function handleKeyDown(ev: KeyboardEvent): void {
    if (!active) return
    if (ev.defaultPrevented) return

    if (ev.key === 'Shift' || ev.key === 'Alt') {
      if (!ev.repeat) setState(withFreeHeight(state, true))
      return
    }

    // 화살표는 누른 채 반복해도 되는 유일한 키다(층을 연속으로 올리는 조작).
    if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
      adjustFreeHeight(ev.key === 'ArrowUp' ? 1 : -1)
      ev.preventDefault()
      return
    }

    if (ev.repeat) return

    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      removeSelected()
      // Backspace는 일부 환경에서 뒤로 가기로 동작한다. 방 하나를 통째로 잃는 사고를 막는다.
      ev.preventDefault()
      return
    }

    switch (ev.key.toLowerCase()) {
      case 'r':
        rotateSelected()
        return
      case 'c':
        cycleColor()
        return
      case 'x':
        removeSelected()
        return
      case 'escape':
        // HUD의 '선택 도구' 버튼과 같은 함수를 탄다. 둘이 갈리면 버튼에 붙은 `Esc` 라벨이 거짓이 된다.
        pickSelectTool()
        return
      default:
        break
    }

    if (selectCatalogByDigit(ev.key)) ev.preventDefault()
  }

  function handleKeyUp(ev: KeyboardEvent): void {
    if (!active || (ev.key !== 'Shift' && ev.key !== 'Alt')) return
    // 한쪽을 떼도 다른 쪽이 눌려 있으면 자유 높이는 유지된다. 이벤트가 그 상태를 이미 담고 있다.
    setState(withFreeHeight(state, isFreeHeightModifier(ev)))
  }

  /** 창이 포커스를 잃으면 keyup을 못 받는다. 수정키가 눌린 채로 굳는 것을 막는다. */
  function handleWindowBlur(): void {
    if (active) setState(withFreeHeight(state, false))
  }

  /* --- 등록 · 정리 -------------------------------------------------------------- */

  // 탭 모드에서는 캔버스 이벤트를 아예 듣지 않는다. 제스처 인식기와 같은 이벤트를 두 곳에서
  // 해석하면 카메라를 끌려던 드래그가 배치로 확정된다(EditorDeps.inputMode 주석).
  const usesPointerInput = (deps.inputMode ?? 'pointer') === 'pointer'

  if (usesPointerInput) {
    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointermove', handlePointerMove)
    canvas.addEventListener('pointerup', handlePointerUp)
    canvas.addEventListener('pointercancel', handlePointerCancel)
    canvas.addEventListener('pointerleave', handlePointerLeave)
  }
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
  window.addEventListener('blur', handleWindowBlur)

  // 시작 방을 화면에 올린다. `setState`는 `room` 참조가 바뀔 때만 동기화하므로 첫 상태는
  // 여기서 한 번 명시적으로 그려야 한다 - 빠뜨리면 소품 400개가 상태에만 있고 화면에는 없다.
  syncObjectViews(ctx, state.room, catalog)
  // 첫 화면을 그린다. 이걸 빠뜨리면 HUD가 빈 상태로 뜬다.
  setState(state)

  return {
    getRoom: () => state.room,
    hud,

    setRoom(room: RoomState): void {
      // 선택·지정은 이전 방의 인스턴스를 가리키고 있다. 그대로 두면 사라진 물건을 집은 채로
      // 다음 조작이 들어와 `unknown-instance` 거부만 반복된다.
      setState({ ...withRoom(state, room), selectedId: null, ghostCell: null })
    },

    setActive(next: boolean): void {
      if (active === next) return
      active = next

      // 드래그 도중에 모드가 바뀌면 손을 뗀 것으로 친다 - 남겨 두면 다음 진입의 첫 pointerup이
      // 옛 드래그의 끝으로 해석돼 엉뚱한 곳에 배치된다.
      drag = null

      if (next) {
        setState(state)
        return
      }

      // 나갈 때는 미리보기를 전부 걷는다. 수색 화면에 초록 고스트 상자가 떠 있으면
      // 그 자체가 판정과 무관한 가짜 단서가 된다.
      setState({ ...state, ghostCell: null, isFreeHeight: false, freeHeightLevel: null })
      ghost.visible = false
      outline.visible = false
    },

    isActive: () => active,

    aimAt,
    commitAim,
    clearAim,
    getAim: () => buildAim(state),
    pickCatalog: selectCatalogEntry,
    pickSelectTool,
    rotateSubject: rotateSelected,
    removeSelected,
    setColor: applyColor,

    dispose(): void {
      if (usesPointerInput) {
        canvas.removeEventListener('pointerdown', handlePointerDown)
        canvas.removeEventListener('pointermove', handlePointerMove)
        canvas.removeEventListener('pointerup', handlePointerUp)
        canvas.removeEventListener('pointercancel', handlePointerCancel)
        canvas.removeEventListener('pointerleave', handlePointerLeave)
      }
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleWindowBlur)

      hud.dispose()

      // Three.js는 GPU 자원을 GC 하지 않는다 - 씬에서 떼는 것만으로는 새어 나간다.
      ghost.removeFromParent()
      disposeObject3D(ghost)
      disposeSelectionOutline(outline)
    },
  }
}
